import { PaymentProviders } from '@/enums';
import { ApiHosts, handleResponseWithNon200Errors, makeGet, makePost } from '@/redux/api/helpers';
import { BankAccount } from '@/types/bankAccount';

/**
 * @category Payments API
 * @see getBankAccounts
 */
type GetBankAccountsParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};

export type GetBankAccountsResponse = {
    error: boolean;
    payload: BankAccount[];
};

/**
 * fetchBankAccounts - fetches all the enabled bidder bank accounts
 * @category Payments API
 * @function getBankAccounts
 * @param {GetBankAccountsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/bankaccounts
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/h4jba4/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/get_bidder__bidderId__bankaccounts
 */
export const getBankAccounts = ({ authToken, bidderId, deployment }: GetBankAccountsParams) =>
    new Promise<GetBankAccountsResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: `bidder/${bidderId}/bankaccounts`,
            authToken,
            deployment,
            path: ApiHosts.Redstripe,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

/**
 * @category Payments API
 * @see postDeleteBankAccount
 */
type PostDeleteBankAccountParams = {
    authToken: string;
    bankAccountId: number;
    bidderId: number;
    deployment: string;
    plaidAccountId?: string;
};

/**
 * deleteBankAccount - deletes a bank account record by backAccountId and bidderId
 * @category Payments API
 * @function postDeleteBankAccount
 * @param {PostDeleteBankAccountParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/bankaccounts/${bankAccountId}
 */
export const postDeleteBankAccount = ({
    authToken,
    bankAccountId,
    bidderId,
    deployment,
    plaidAccountId,
}: PostDeleteBankAccountParams) =>
    new Promise<void>((resolve, reject) => {
        const request = makePost({
            apiPath: `bidder/${bidderId}/bankaccounts/${bankAccountId}`,
            authToken,
            deployment,
            path: ApiHosts.Redstripe,
        });
        request.send({ plaidAccountId });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

/**
 * @category Payments API
 * @see postFetchPlaidLinkToken
 */
export type PostFetchPlaidLinkTokenParams = {
    accessToken?: string;
    authToken: string;
    bidderId: number;
    deployment: string;
};

export type PostFetchPlaidLinkTokenResponse = {
    error: boolean;
    payload: string;
};

/**
 * fetchPlaidLinkToken - gets a plaid temporary token for a bidder
 * @category Payments API
 * @function postFetchPlaidLinkToken
 * @param {PostFetchPlaidLinkTokenParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/plaid/createlinktoken
 */
export const postFetchPlaidLinkToken = ({
    accessToken,
    authToken,
    bidderId,
    deployment,
}: PostFetchPlaidLinkTokenParams): Promise<PostFetchPlaidLinkTokenResponse> =>
    new Promise<PostFetchPlaidLinkTokenResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: 'plaid/createlinktoken',
            authToken,
            deployment,
            path: ApiHosts.Payment,
        });
        request.send({
            accessToken,
            bidderId,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

/**
 * @category Payments API
 * @see postCreatePlaidBankAccount
 */
export type PostCreatePlaidBankAccountParams = {
    accessToken?: string;
    accountId: string;
    authToken: string;
    bidderId: number;
    deployment: string;
    firstName: string;
    lastName: string;
    token: string;
};

export type PostCreatePlaidBankAccountResponse = {
    error: boolean;
    payload: BankAccount;
};

/**
 * postCreatePlaidBankAccount - sets a public token for a bidder
 * @category Payments API
 * @function postCreatePlaidBankAccount
 * @param {PostCreatePlaidBankAccountParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/bidder/${bidderId}/bankaccounts/verifiedbyplaid
 */
export const postCreatePlaidBankAccount = ({
    accessToken,
    accountId,
    authToken,
    bidderId,
    deployment,
    firstName,
    lastName,
    token,
}: PostCreatePlaidBankAccountParams): Promise<PostCreatePlaidBankAccountResponse> =>
    new Promise<PostCreatePlaidBankAccountResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: `bidder/${bidderId}/bankaccounts/verifiedbyplaid`,
            authToken,
            deployment,
            path: ApiHosts.Redstripe,
        });
        request.send({
            accessToken,
            accountId,
            defaultAccount: true,
            firstName,
            lastName,
            providerId: PaymentProviders.Payrix,
            token,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });
